import { useEffect, useRef, useState } from "react";

/**
 * Floating/dynamic mobile controls (Minecraft PE / Fall Guys style).
 *
 * Left half of the screen is the stick zone — the first touch there spawns
 * a virtual joystick centered on the finger. If the finger drifts beyond the
 * max radius, the base follows the finger so the next input direction stays
 * relative to where the thumb actually is.
 *
 * Right half is the action zone. Tap = jump. Big visible Jump button
 * renders as an affordance.
 */
interface Props {
  onMove: (x: number, y: number) => void;
  onJump: () => void;
  /** Kept for API compatibility (parent still passes it) — unused. */
  airborne?: boolean;
  /** Inner deadzone (0..0.4). Smaller = more sensitive near center. */
  deadzone?: number;
  /** Movement sensitivity (0.25..1.5). 1 = linear; <1 adds expo curve. */
  sensitivity?: number;
  /** Optional getter for jump-cooldown progress (1 = just jumped, 0 = ready).
   *  When provided, the JUMP button shows a recharge wipe + dim. */
  jumpCooldown01?: () => number;
}

interface StickState {
  pointerId: number;
  baseX: number;
  baseY: number;
  knobX: number;
  knobY: number;
  visible: boolean;
}

const MAX_RADIUS = 64; // px — max thumb travel from base before base follows.

export function TouchControls({ onMove, onJump, deadzone = 0.12, sensitivity = 1.0, jumpCooldown01 }: Props) {
  const jumpBtnRef = useRef<HTMLButtonElement>(null);
  const jumpCooldownRef = useRef(jumpCooldown01);
  jumpCooldownRef.current = jumpCooldown01;
  const deadzoneRef = useRef(deadzone);
  const sensitivityRef = useRef(sensitivity);
  deadzoneRef.current = THREE_clamp(deadzone, 0, 0.4);
  sensitivityRef.current = THREE_clamp(sensitivity, 0.25, 1.5);
  const rootRef = useRef<HTMLDivElement>(null);
  const stickRef = useRef<StickState | null>(null);
  const rightPointer = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    startedAt: number;
  } | null>(null);
  const [stick, setStick] = useState<StickState | null>(null);
  const lastEmit = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    const isLeftHalf = (clientX: number) => clientX < window.innerWidth / 2;

    const emit = (x: number, y: number) => {
      if (Math.hypot(x - lastEmit.current.x, y - lastEmit.current.y) < 0.005) return;
      lastEmit.current = { x, y };
      onMove(x, y);
    };

    const setKnob = (s: StickState) => {
      stickRef.current = s;
      setStick({ ...s });
    };

    const updateStick = (clientX: number, clientY: number) => {
      const s = stickRef.current;
      if (!s) return;
      let dx = clientX - s.baseX;
      let dy = clientY - s.baseY;
      const dist = Math.hypot(dx, dy);
      if (dist > MAX_RADIUS) {
        // Drag-pad behavior: base follows the finger, knob stays at the rim.
        const overshoot = dist - MAX_RADIUS;
        const ux = dx / dist;
        const uy = dy / dist;
        s.baseX += ux * overshoot;
        s.baseY += uy * overshoot;
        dx = ux * MAX_RADIUS;
        dy = uy * MAX_RADIUS;
      }
      // Clamp the joystick base inside the viewport with a margin so it can
      // never get stuck off-screen (e.g. when the finger swipes off the edge
      // and pointerup doesn't fire cleanly on iOS).
      const margin = MAX_RADIUS + 12;
      const maxX = window.innerWidth - margin;
      const maxY = window.innerHeight - margin;
      if (s.baseX < margin) s.baseX = margin;
      if (s.baseX > maxX) s.baseX = maxX;
      if (s.baseY < margin) s.baseY = margin;
      if (s.baseY > maxY) s.baseY = maxY;
      s.knobX = dx;
      s.knobY = dy;
      setKnob(s);
      // Normalized vector with inner deadzone -> smooth ramp -> sensitivity curve.
      const dz = deadzoneRef.current;
      const sens = sensitivityRef.current;
      const mag = Math.hypot(dx, dy) / MAX_RADIUS;
      if (mag < dz) {
        emit(0, 0);
        return;
      }
      const ramp = (mag - dz) / (1 - dz);
      const shaped = Math.pow(ramp, 1.05 / sens);
      const k = shaped / mag;
      emit((dx / MAX_RADIUS) * k, (-dy / MAX_RADIUS) * k);
    };

    const releaseStick = () => {
      stickRef.current = null;
      setStick(null);
      emit(0, 0);
    };

    const onPointerDown = (e: PointerEvent) => {
      // Ignore taps on real interactive elements (HUD pause button, etc).
      const target = e.target as HTMLElement | null;
      if (target?.closest("button, a, input, [data-touch-passthrough]")) return;
      // Block default so iOS doesn't trigger scroll/gestures.
      e.preventDefault();
      if (isLeftHalf(e.clientX)) {
        if (stickRef.current) return; // already steering with another finger
        setKnob({
          pointerId: e.pointerId,
          baseX: e.clientX,
          baseY: e.clientY,
          knobX: 0,
          knobY: 0,
          visible: true,
        });
        root.setPointerCapture(e.pointerId);
      } else {
        if (rightPointer.current) return;
        rightPointer.current = {
          pointerId: e.pointerId,
          startX: e.clientX,
          startY: e.clientY,
          startedAt: performance.now(),
        };
        root.setPointerCapture(e.pointerId);
        // Fire the jump RIGHT NOW on press so press-and-hold works (release
        // alone won't trigger the previous tap-only branch if the press
        // lasted longer than 320 ms). Most platformer touch controls
        // jump on touchstart for snappy feel anyway.
        onJump();
        vibrate(8);
      }
    };

    const onPointerMove = (e: PointerEvent) => {
      const s = stickRef.current;
      if (s && e.pointerId === s.pointerId) {
        updateStick(e.clientX, e.clientY);
      }
    };

    const onPointerUp = (e: PointerEvent) => {
      const s = stickRef.current;
      if (s && e.pointerId === s.pointerId) {
        releaseStick();
        try { root.releasePointerCapture(e.pointerId); } catch {}
        return;
      }
      const rp = rightPointer.current;
      if (rp && e.pointerId === rp.pointerId) {
        try { root.releasePointerCapture(e.pointerId); } catch {}
        rightPointer.current = null;
        // Jump already fired on pointerdown — nothing to do on release.
      }
    };

    const onPointerCancel = (e: PointerEvent) => {
      const s = stickRef.current;
      if (s && e.pointerId === s.pointerId) releaseStick();
      const rp = rightPointer.current;
      if (rp && e.pointerId === rp.pointerId) rightPointer.current = null;
    };

    // Safety net: if the document loses focus / visibility (user switches tab,
    // gets a notification, etc.) OR the pointer capture is silently dropped
    // by iOS Safari, release the stick so input isn't left held.
    const onLostCapture = (e: PointerEvent) => {
      const s = stickRef.current;
      if (s && e.pointerId === s.pointerId) releaseStick();
    };
    const onVisibilityChange = () => {
      if (document.hidden && stickRef.current) releaseStick();
    };
    const onWindowBlur = () => {
      if (stickRef.current) releaseStick();
    };

    root.addEventListener("pointerdown", onPointerDown);
    root.addEventListener("pointermove", onPointerMove);
    root.addEventListener("pointerup", onPointerUp);
    root.addEventListener("pointercancel", onPointerCancel);
    root.addEventListener("lostpointercapture", onLostCapture);
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("blur", onWindowBlur);
    return () => {
      root.removeEventListener("pointerdown", onPointerDown);
      root.removeEventListener("pointermove", onPointerMove);
      root.removeEventListener("pointerup", onPointerUp);
      root.removeEventListener("pointercancel", onPointerCancel);
      root.removeEventListener("lostpointercapture", onLostCapture);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("blur", onWindowBlur);
    };
  }, [onMove, onJump]);

  // Drive the JUMP button's recharge wipe directly via a CSS var so we
  // never re-render React 60×/sec. Idle (getter absent or returns 0) is a
  // no-op once the class clears.
  useEffect(() => {
    if (!jumpCooldown01) return;
    let raf = 0;
    let wasCooling = false;
    const tick = () => {
      raf = requestAnimationFrame(tick);
      const btn = jumpBtnRef.current;
      if (!btn) return;
      const cd = jumpCooldownRef.current?.() ?? 0;
      const cooling = cd > 0.001;
      if (cooling) {
        btn.style.setProperty("--cd", cd.toFixed(3));
        if (!wasCooling) btn.classList.add("is-cooling");
      } else if (wasCooling) {
        btn.classList.remove("is-cooling");
        btn.style.setProperty("--cd", "0");
      }
      wasCooling = cooling;
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [jumpCooldown01]);

  return (
    <div ref={rootRef} className="touch-controls touch-controls-v2" aria-hidden>
      <div className="touch-zone touch-zone-left" />
      <div className="touch-zone touch-zone-right" />
      {stick && (
        <div
          className="touch-stick-base"
          style={{ left: stick.baseX, top: stick.baseY }}
        >
          <div
            className="touch-stick-knob"
            style={{ transform: `translate(${stick.knobX}px, ${stick.knobY}px)` }}
          />
        </div>
      )}
      <button
        ref={jumpBtnRef}
        type="button"
        className="touch-jump-btn"
        onPointerDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onJump();
          vibrate(15);
        }}
        aria-label="Jump"
      >
        <span className="touch-jump-arrow" aria-hidden>↑</span>
        <span className="touch-jump-label">JUMP</span>
      </button>
    </div>
  );
}

function THREE_clamp(v: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, v));
}

function vibrate(ms: number) {
  try {
    navigator.vibrate?.(ms);
  } catch {}
}
